import { useMemo } from "react";
import RepeatableObjectField, {
  type FieldDef,
} from "@components/RepeatableField/RepeatableObjectField";
import type { NormalGatepassFormData } from "@features/gatepass/schema/normalGatepassFormSchema";
import { useFormattedReference } from "@hooks/useFormattedReference";
import { type Location } from "@services/reference-data/types";
import { REFERENCE_DATA_TYPES } from "@services/referenceDataApi";
import { t } from "i18next";
import { FormProvider, type UseFormReturn } from "react-hook-form";
import { useOutletContext } from "react-router-dom";
import { useNormalGatepassFormStore } from "../../store/useNormalGatepassForm.store";

export interface FormContext {
  formData: UseFormReturn<NormalGatepassFormData>;
  updateFormData: (key: string, value: unknown) => void;
}
const TRANSLATION_KEY = "gatepass";

const ordinalSuffix = (n: number) => {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return "th";
  switch (n % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
};

const locationLabelByIndex = (index: number) => {
  if (index === 0) return t(`${TRANSLATION_KEY}.primary-location`);
  if (index === 1) return t(`${TRANSLATION_KEY}.secondary-location`);
  if (index === 2) return t(`${TRANSLATION_KEY}.third-location`);

  const n = index + 1;
  return `${n}${ordinalSuffix(n)} Location`;
};

const Locations = () => {
  const { formData } = useOutletContext<FormContext>();
  const { setField } = useNormalGatepassFormStore();
  const { options: locations } = useFormattedReference<Location>(
    REFERENCE_DATA_TYPES.LOCATIONS,
    "bff-gatepass",
  );

  const formattedLocationsOptions = useMemo(
    () =>
      locations.map(({ locationName }) => ({
        label: locationName,
        value: locationName,
      })),
    [locations],
  );
  const defs: FieldDef[] = [
    {
      key: "location",
      label: locationLabelByIndex,
      type: "autocomplete",
      options: formattedLocationsOptions,
    },
    { key: "entryPurpose", label: t(`${TRANSLATION_KEY}.entry-purpose`), type: "textarea" },
  ];

  return (
    <FormProvider {...formData}>
      <RepeatableObjectField
        name="locations"
        numberOfColumns={1}
        sectionLabel={t(`${TRANSLATION_KEY}.locations`)}
        label={t(`${TRANSLATION_KEY}.location-entry`)}
        hideItemIndexLabel
        fields={defs}
        minItems={1}
        addButtonLabel={t(`${TRANSLATION_KEY}.add-location`)}
        onFieldChange={(_key, arr: unknown[]) => {
          setField("locations", arr as { location: string; entryPurpose: string }[]);
        }}
        testId="friends-repeatable-field"
      />
    </FormProvider>
  );
};

export default Locations;
